import { Check, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import React, { useEffect, useId, useRef, useState } from 'react';
import { CustomSelect } from '../../../../../shared/components/CustomSelect';
import { formatTeamRosterRoleDisplay } from '../../../../../shared/teamRosterRoleDisplay';
import type { RosterSortBy, RosterSortDir } from '../../TeamTabContext';

export interface TeamRosterFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  hideDeactivated: boolean;
  onHideDeactivatedChange: (value: boolean) => void;
  availableRoles: string[];
  selectedRoles: string[];
  onSelectedRolesChange: (roles: string[]) => void;
  sortBy: RosterSortBy;
  onSortByChange: (value: RosterSortBy) => void;
  sortDir: RosterSortDir;
  onSortDirToggle: () => void;
  filteredCount: number;
  totalCount: number;
}

export const TeamRosterFilterBar: React.FC<TeamRosterFilterBarProps> = ({
  search,
  onSearchChange,
  hideDeactivated,
  onHideDeactivatedChange,
  availableRoles,
  selectedRoles,
  onSelectedRolesChange,
  sortBy,
  onSortByChange,
  sortDir,
  onSortDirToggle,
  filteredCount,
  totalCount,
}) => {
  const [rolesOpen, setRolesOpen] = useState(false);
  const rolesRef = useRef<HTMLDivElement>(null);
  const switchId = useId();
  const searchId = useId();

  useEffect(() => {
    if (!rolesOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (rolesRef.current && !rolesRef.current.contains(e.target as Node)) {
        setRolesOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [rolesOpen]);

  const toggleRole = (role: string) => {
    if (selectedRoles.includes(role)) {
      onSelectedRolesChange(selectedRoles.filter((r) => r !== role));
    } else {
      onSelectedRolesChange([...selectedRoles, role]);
    }
  };

  const rolesLabel =
    selectedRoles.length === 0
      ? 'All roles'
      : selectedRoles.length === 1
        ? formatTeamRosterRoleDisplay(selectedRoles[0])
        : `${selectedRoles.length} roles`;

  return (
    <div className="team-roster-filter-bar">
      <div className="team-roster-filter-bar__row">
        <div className="team-roster-filter-search">
          <Search size={16} className="team-roster-filter-search__icon" aria-hidden />
          <input
            id={searchId}
            type="search"
            className="team-roster-filter-search__input"
            placeholder="Search name, email, Jira, or role"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search team roster"
          />
        </div>

        <button
          type="button"
          id={switchId}
          className="team-roster-filter-switch"
          role="switch"
          aria-checked={hideDeactivated}
          aria-label="Hide deactivated people (no Jira username)"
          title="Hide people with no Jira username"
          onClick={() => onHideDeactivatedChange(!hideDeactivated)}
        >
          <span className="team-roster-filter-switch__track" aria-hidden>
            <span className="team-roster-filter-switch__thumb" />
          </span>
          <span className="team-roster-filter-switch__label">Hide deactivated</span>
        </button>

        <div className="team-roster-filter-roles" ref={rolesRef}>
          <button
            type="button"
            className={`team-roster-filter-roles__trigger${rolesOpen ? ' is-open' : ''}${selectedRoles.length > 0 ? ' is-active' : ''}`}
            aria-haspopup="listbox"
            aria-expanded={rolesOpen}
            aria-label="Filter by role"
            onClick={() => setRolesOpen((v) => !v)}
          >
            <span className="team-roster-filter-roles__value">{rolesLabel}</span>
            <ChevronDown size={16} className="team-roster-filter-roles__chevron" aria-hidden />
          </button>
          {rolesOpen && (
            <div
              className="team-roster-filter-roles__menu"
              role="listbox"
              aria-multiselectable="true"
            >
              <p className="team-roster-filter-roles__hint">
                Empty = everyone, including people not yet on this product.
              </p>
              {availableRoles.length === 0 ? (
                <p className="team-roster-filter-roles__empty">No roles yet</p>
              ) : (
                availableRoles.map((role) => {
                  const checked = selectedRoles.includes(role);
                  return (
                    <button
                      key={role}
                      type="button"
                      role="option"
                      aria-selected={checked}
                      className={`team-roster-filter-roles__option${checked ? ' is-selected' : ''}`}
                      onClick={() => toggleRole(role)}
                    >
                      <span className="team-roster-filter-roles__check" aria-hidden>
                        {checked ? <Check size={14} /> : null}
                      </span>
                      {formatTeamRosterRoleDisplay(role)}
                    </button>
                  );
                })
              )}
              {selectedRoles.length > 0 && (
                <button
                  type="button"
                  className="team-roster-filter-roles__clear"
                  onClick={() => onSelectedRolesChange([])}
                >
                  Clear roles
                </button>
              )}
            </div>
          )}
        </div>

        <CustomSelect
          value={sortBy}
          options={[
            { value: 'name', label: 'Sort by name' },
            { value: 'role', label: 'Sort by role' },
            { value: 'capacity', label: 'Sort by hours/day' },
          ]}
          onChange={(val) => onSortByChange(val as RosterSortBy)}
          className="team-roster-filter-sort"
        />
        <button
          type="button"
          className="team-roster-filter-sortdir"
          onClick={onSortDirToggle}
          title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
          aria-label={sortDir === 'asc' ? 'Sort ascending' : 'Sort descending'}
        >
          {sortDir === 'asc' ? (
            <ChevronUp size={18} aria-hidden />
          ) : (
            <ChevronDown size={18} aria-hidden />
          )}
        </button>
      </div>

      {selectedRoles.length > 0 && (
        <div className="team-roster-filter-chips" aria-label="Selected roles">
          {selectedRoles.map((role) => (
            <button
              key={role}
              type="button"
              className="team-roster-filter-chip"
              onClick={() => toggleRole(role)}
              aria-label={`Remove ${formatTeamRosterRoleDisplay(role)} filter`}
            >
              {formatTeamRosterRoleDisplay(role)}
              <X size={12} aria-hidden />
            </button>
          ))}
        </div>
      )}

      <p className="team-roster-filter-count">
        {filteredCount === totalCount
          ? `${totalCount} people`
          : `${filteredCount} of ${totalCount} people`}
      </p>
    </div>
  );
};
